import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import SearchHistory from './SearchHistory.jsx';
import { lang, __ } from "../../Lang.js";
import debounce from 'lodash.debounce';

const SearchBar = ({ searchQuery, defaultSearchQuery, setSearchQuery, updateGrid, setLoading}) => {

    /* The search bar triggers autocomplete, not search results */
    const [searchBarQuery, setSearchBarQuery] = useState(searchQuery || defaultSearchQuery || '');
    /* A direct query is used to fetch search results */
    const [searchDirectQuery, setSearchDirectQuery] = useState('');

    const [autocompleteResults, setAutocompleteResults] = useState([]);
    const [buttonLabel, setButtonLabel] = useState(__('Search'));
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchHistory, setSearchHistory] = useState([]);

    const searchParams = new URLSearchParams(window.location.search);

    // Memoized search history management
    const addSearchToHistory = (term) => {
        const cleaned = term.trim();
        if (cleaned.length < 3) return;

        const normalized = cleaned.toLowerCase();
        const key = 'twi_search_history';

        let history = JSON.parse(localStorage.getItem(key)) ?? [];
        history = history.filter(item => item.toLowerCase() !== normalized);
        history.unshift(normalized);
        history = history.slice(0, 5);

        localStorage.setItem(key, JSON.stringify(history));
        setSearchHistory(history);
    };


    // load local storage search history on mount
    useEffect(() => {
        let savedHistory = localStorage.getItem('twi_search_history');
        if (savedHistory) {
            setSearchHistory(JSON.parse(savedHistory));
        }
    }, []);

    // Search bar query triggers autocomplete
    useEffect(() => {
        if (searchBarQuery && !searchLoading) {
            handleAutocomplete(searchBarQuery);
        }
    }, [searchBarQuery]);

    // Direct query triggers search results
    // this includes:
    // - clicking on an autocomplete result
    // - clicking on a history item
    // - pressing enter in the search bar
    // - clicking the search button
    useEffect(() => {
        if (searchDirectQuery) {
            fetchSearchResults(searchDirectQuery);
        }
    }, [searchDirectQuery]);

    // debounce the autocomplete function to limit the frequency of API calls
    const handleAutocomplete = debounce((searchQuery) => {
        handleAutocompleteAsync(searchQuery);
    }, 500);

  const handleAutocompleteAsync = async(searchQuery) => {
    if (searchQuery === false) {
        setAutocompleteResults([]);
        return;
    }

    // minimum length for autocomplete is 3 characters
    const trimmed = searchQuery.trim();
    if (trimmed.length < 3) return;

    try {
        const response = await axios.get(`/api/autocomplete`, {
          params: {
            term: searchQuery,
            lang: lang,
          },
        });

        if (response.data.length > 0) {
          setAutocompleteResults(response.data);
        } else {
          setAutocompleteResults([]);
        }
      } catch (err) {
        setAutocompleteResults([]);
      }
    };

    // convenience function to handle search button click
    // note this does not stop event propagation
    const handleSearch = () => {
        setSearchDirectQuery(searchBarQuery);
    };

    const fetchSearchResults = (query) => {
        const trimmed = query.trim();
        if (trimmed.length < 3) return;

        //update url bar but do NOT navigate
        searchParams.set('search', trimmed);
        const newUrl = 'https://' + window.location.hostname + window.location.pathname + '?' + searchParams.toString();
        window.history.pushState({}, '', newUrl);

        async function doFetch(query) {
            try {
                setSearchLoading(true);
                setLoading(true);
                
                const apiUrl = `/api/v2/twi/search?search=${encodeURIComponent(query)}`;
                const response = await fetch(apiUrl);
                const data = await response.json();

                if (data.status === 'success') {

                    let playlists = data.playlists ?? [];
                    let videos = data.videos ?? [];
                    let total_count = data.pagination?.total ?? 0;
                    let total_pages = data.pagination?.total_pages ?? 1;
                    let current_page = data.pagination?.page ?? 1;
                    updateGrid(videos, total_count, total_pages, current_page, playlists);

                    // add to history on success
                    addSearchToHistory(query);
                } else {
                    console.error('Search API error:', data);
                }
            } catch (error) {
                console.error('Error fetching search results:', error);
            } finally {
                setLoading(false);
                setSearchLoading(false);
                setSearchQuery(query);
            }
        }
        doFetch(query);
    };
  return (
    <Box sx={{ width: '100%' }}>
        <SearchIcon sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary' }} />
    <Card elevation={2} sx={{ mt: 4, mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="stretch">
          {/* Search Input */}
          <Grid item xs={12} md={9}>
            <Autocomplete
              freeSolo
              options={autocompleteResults}
                      // value of the TextField that is under React control
            inputValue={searchBarQuery}
            // what to do when the TextField value changes
            onInputChange={(_e, value, _reason) => {
                if (window.location.search.includes('search=')) {
                    setButtonLabel(__('New Search'));
                }
                setSearchBarQuery(value ?? '');
            }}
            // what to do when the autocomplete menu is interacted with
            onChange={(_e, value, reason) => {
                if (reason === 'selectOption') { //user selected an option
                    setSearchDirectQuery(value);
                }
            }}
              renderOption={({ key, ...props }, option) => {
                const safe = searchBarQuery.replace(/ /g, '&nbsp;');
                const highlighted = option.replace(
                  searchBarQuery,
                  `<strong>${safe}</strong>`
                );
                return (
                  <li key={key} {...props}>
                    <span dangerouslySetInnerHTML={{ __html: highlighted }} />
                  </li>
                );
              }}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                    padding: 0,
                },
                '& .MuiAutocomplete-input': {
                    paddingLeft: '3.5rem', // space for the search icon
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="search"
                  fullWidth
                  label={__('Search videos...')}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                    if (e.key === 'Escape') setQuery('');
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off',
                  }}
                  sx={{
                    fontSize: '2rem',
                    minHeight: '4rem',
                    '& .MuiAutocomplete-endAdornment': {
                      padding: '0',
                      height: '36px',
                      position: 'absolute',
                      right: '9px',
                      top: 'calc(50% - 8px)',
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                      visibility: 'visible',
                      width: '36px',
                      height: '36px',
                    },
                    '& .MuiInputBase-root': {
                      paddingLeft: '0',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '1.5rem',
                      height: '4rem',
                    },
                    '& .MuiAutocomplete-input': {
                      padding: '0.5rem 1rem!important',
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              onClick={() => handleSearch()}
              disabled={searchLoading}
              sx={{
                mt: { xs: 1, md: 0 },
                padding: '1rem 2rem',
                height: '4rem',
                fontSize: '1rem',
                width: '100%',
                minHeight: '4rem',
                backgroundColor: 'primary.main',
              }}
            >
              {searchLoading ? <CircularProgress size={24} /> : buttonLabel}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

        {/* Search History */}
        {searchHistory.length > 0 && (
          <Grid container spacing={2} sx={{ mt: 2, 'paddingTop': 0, }}>
            <Grid item xs={12}>
              <SearchHistory
                searchHistory={searchHistory}
                onHistoryItemClick={(term) => setSearchDirectQuery(term)}
              />
            </Grid>
          </Grid>
        )}

    </Box>
  );

};

export default SearchBar;